import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface CategoryTileOption {
  value: string
  label: string
  count: number
  glyph: string
  /** CSS var/color for the tile's tinted wash background. */
  tintSoft: string
  /** CSS var/color for the glyph. */
  tintInk: string
}

export interface CategoryTilesProps {
  options: CategoryTileOption[]
  value: string
  onChange: (value: string) => void
}

const Grid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 12,
})

const Tile = styled('button', { shouldForwardProp: (p) => p !== '$active' })<{ $active?: boolean }>(
  ({ $active }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    position: 'relative',
    height: 96,
    borderRadius: 16,
    overflow: 'hidden',
    background: 'var(--ds-canvas)',
    boxShadow: $active ? 'inset 0 0 0 2px var(--ds-ink)' : 'inset 0 0 0 1px var(--ds-border)',
    cursor: 'pointer',
    padding: '13px 14px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    textAlign: 'left',
    transition: `transform var(--ds-duration-normal) var(--ds-ease-out), box-shadow var(--ds-duration-normal) var(--ds-ease)`,
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: $active ? 'inset 0 0 0 2px var(--ds-ink)' : 'inset 0 0 0 1px var(--ds-border-strong), var(--ds-shadow-lift)',
    },
    '&:hover .tile-wash': { opacity: 1 },
  }),
)

const Wash = styled(Box, { shouldForwardProp: (p) => p !== '$tint' && p !== '$active' })<{
  $tint: string
  $active?: boolean
}>(({ $tint, $active }) => ({
  position: 'absolute',
  inset: 0,
  background: $tint,
  opacity: $active ? 1 : 0.6,
  transition: `opacity var(--ds-duration-normal) var(--ds-ease)`,
  pointerEvents: 'none',
}))

const Glyph = styled('span')({ position: 'relative', fontSize: 17, lineHeight: 1 })

const Label = styled(Typography)({
  position: 'relative',
  fontSize: 13,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  color: 'var(--ds-ink)',
  display: 'block',
})

const Count = styled(Typography)({
  position: 'relative',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.1em',
  color: 'var(--ds-ink-subtle)',
  marginTop: 3,
  display: 'block',
})

/**
 * Spotify-browse-style tinted tiles — each browsing axis (All / Walkable /
 * Albums / 3D Galleries) owns one brand tint, carried later onto the same
 * item's tag in `DiscoverCard`, so color reads as navigation rather than
 * decoration. Selecting a tile drives the grid filter below.
 */
export function CategoryTiles({ options, value, onChange }: CategoryTilesProps) {
  return (
    <Grid role='radiogroup'>
      {options.map((opt) => (
        <Tile
          key={opt.value}
          type='button'
          role='radio'
          aria-checked={value === opt.value}
          $active={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          <Wash className='tile-wash' $tint={opt.tintSoft} $active={value === opt.value} />
          <Glyph style={{ color: opt.tintInk }}>{opt.glyph}</Glyph>
          <Box>
            <Label>{opt.label}</Label>
            <Count>{opt.count} SHARE{opt.count === 1 ? '' : 'S'}</Count>
          </Box>
        </Tile>
      ))}
    </Grid>
  )
}
